/**
 * ForgeClientSelector
 * 
 * Dropdown component for selecting a Forge Client to map to an Organization.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import forgeService from '../../services/forgeService';
import { ForgeClient } from '../../types';

interface ForgeClientSelectorProps {
  selectedClientId?: string | null;
  onSelect: (clientId: string | null, client: ForgeClient | null) => void;
  disabled?: boolean;
  className?: string;
}

export const ForgeClientSelector: React.FC<ForgeClientSelectorProps> = ({
  selectedClientId,
  onSelect,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<ForgeClient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClients = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await forgeService.listClients();
        setClients(data);
      } catch (err: any) {
        setError(err?.message || t('forge.selector.errorLoadingClients', 'Failed to load Forge clients'));
        console.error('Error fetching Forge clients:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, [t]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onSelect(null, null);
    } else {
      const client = clients.find(c => c.client_id === value);
      onSelect(value, client || null);
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-muted-foreground mb-1">
        {t('forge.selector.clientLabel', 'Forge Client')}
      </label>
      <select
        value={selectedClientId || ''}
        onChange={handleChange}
        disabled={disabled || isLoading}
        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
      >
        <option value="">
          {isLoading
            ? t('global.loading', 'Loading...')
            : t('forge.selector.selectClient', '-- Select a Forge Client --')}
        </option>
        {clients.map((client) => (
          <option key={client.client_id} value={client.client_id}>
            {client.name}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-xs text-destructive mt-1">{error}</p>
      )}
    </div>
  );
};

export default ForgeClientSelector;
